import { forwardRef, useCallback, useId, useLayoutEffect, useRef, useState, type ChangeEvent, type InputHTMLAttributes, type ReactNode } from 'react'
import { cx } from './cx'
import { IconButton } from './Button'
import { FieldShell } from './FieldShell'
import { FloatingPanel } from './FloatingPanel'
import { CalendarPicker } from './CalendarPicker'
import { applyMask, DATE_KINDS, formatValue, MASKS, outOfRange, parseText, PLACEHOLDERS, type DateKind } from './dateFormat'

export const isDateKind = (t: string | undefined): t is DateKind => Boolean(t && DATE_KINDS.includes(t))

export interface DateFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'prefix' | 'size' | 'children'> {
  kind: DateKind
  label: string
  variant?: 'outlined' | 'filled' | undefined
  supportingText?: ReactNode | undefined
  error?: string | undefined
  labelHidden?: boolean | undefined
  leadingIcon?: string | undefined
}

function setNativeValue(input: HTMLInputElement, value: string) {
  const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set
  setter?.call(input, value)
  input.dispatchEvent(new Event('input', { bubbles: true }))
}

const noop = () => undefined

/**
 * Campo data/ora M3 in italiano: testo con maschera (gg/mm/aaaa, mm/aaaa, hh:mm, gg/mm/aaaa hh:mm) e calendario.
 * Il valore verso le pagine e' sempre quello ISO dell'<input> nativo (yyyy-mm-dd, yyyy-mm, hh:mm, yyyy-mm-ddThh:mm):
 * lo porta un <input> gemello nascosto (name, required, onChange con e.target.value).
 */
export const DateField = forwardRef<HTMLInputElement, DateFieldProps>(function DateField(
  {
    kind,
    label,
    variant = 'outlined',
    supportingText,
    error,
    labelHidden,
    leadingIcon,
    id: idProp,
    className,
    disabled,
    placeholder,
    autoFocus,
    readOnly,
    onFocus,
    onBlur,
    onKeyDown,
    'aria-describedby': ariaDescribedBy,
    ...rest
  },
  ref,
) {
  const autoId = useId()
  const id = idProp ?? autoId
  const msgId = `${id}-msg`
  const labelId = `${id}-label`
  const mask = MASKS[kind]
  const hasMessage = Boolean(error || supportingText)
  const describedBy = [ariaDescribedBy, hasMessage ? msgId : undefined].filter(Boolean).join(' ') || undefined
  const min = typeof rest.min === 'string' ? rest.min : undefined
  const max = typeof rest.max === 'string' ? rest.max : undefined

  const mirror = useRef<HTMLInputElement | null>(null)
  const visible = useRef<HTMLInputElement | null>(null)
  const boxRef = useRef<HTMLDivElement | null>(null)
  const seen = useRef<string | null>(null)
  const [text, setText] = useState('')
  const [open, setOpen] = useState(false)

  // Il gemello nascosto e' la fonte del valore (controllato o no): se cambia da fuori, riscrive il testo.
  useLayoutEffect(() => {
    const v = mirror.current?.value ?? ''
    if (v !== seen.current) {
      seen.current = v
      setText(formatValue(kind, v))
    }
  })

  const setRefs = (el: HTMLInputElement | null) => {
    mirror.current = el
    if (typeof ref === 'function') ref(el)
    else if (ref) ref.current = el
  }

  const commit = (iso: string) => {
    const m = mirror.current
    if (!m || m.value === iso) return
    seen.current = iso
    setNativeValue(m, iso)
  }

  const typedIso = parseText(kind, text)
  const invalid = text !== '' && (typedIso === null ? text.length >= mask.length : outOfRange(kind, typedIso, min, max))

  const onInput = (e: ChangeEvent<HTMLInputElement>) => {
    const masked = applyMask(e.target.value, mask)
    setText(masked)
    const iso = parseText(kind, masked)
    if (masked === '') commit('')
    else if (iso !== null && !outOfRange(kind, iso, min, max)) commit(iso)
    else commit('')
  }

  const closePicker = useCallback(() => setOpen(false), [])
  const pickDate = (iso: string) => {
    setOpen(false)
    let next = iso
    if (kind === 'month') next = iso.slice(0, 7)
    if (kind === 'datetime-local') {
      const cur = mirror.current?.value ?? ''
      const time = /[T ](\d{2}:\d{2})/.exec(cur)?.[1] ?? '09:00'
      next = `${iso}T${time}`
    }
    setText(formatValue(kind, next))
    commit(next)
    visible.current?.focus()
  }

  const hasPicker = kind !== 'time'
  const pickerValue = kind === 'month' ? (typedIso ?? '') : (typedIso ?? '').slice(0, 10)
  const ph = placeholder && placeholder.trim() ? placeholder : PLACEHOLDERS[kind]
  const mirrorControlled = rest.value !== undefined

  return (
    <FieldShell
      id={id}
      labelId={labelId}
      containerRef={boxRef}
      label={label}
      labelHidden={labelHidden}
      variant={variant}
      supportingText={supportingText}
      error={error ?? (invalid ? (kind === 'time' ? 'Ora non valida' : 'Data non valida') : undefined)}
      disabled={disabled}
      floated
      leadingIcon={leadingIcon}
      trailingIcon={undefined}
      prefix={undefined}
      suffix={undefined}
      multiline={false}
      className={cx('md-field--date', `md-field--${kind}`, hasPicker && 'md-field--trailing', className)}
      describedBy={hasMessage || invalid ? msgId : undefined}
      trailingAction={
        hasPicker ? (
          <IconButton
            icon="calendar_today"
            size="sm"
            aria-label={kind === 'month' ? 'Scegli il mese' : 'Scegli la data'}
            aria-haspopup="dialog"
            aria-expanded={open}
            disabled={disabled || readOnly}
            className="md-field__picker-button"
            onClick={() => setOpen((v) => !v)}
          />
        ) : null
      }
    >
      <input
        ref={visible}
        id={`${id}-text`}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        autoFocus={autoFocus}
        readOnly={readOnly}
        disabled={disabled}
        placeholder={ph}
        value={text}
        maxLength={mask.length}
        aria-labelledby={labelId}
        aria-invalid={error || invalid ? true : undefined}
        aria-describedby={describedBy}
        aria-required={rest.required || undefined}
        className="md-field__input md-field__date-input"
        onChange={onInput}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={(e) => {
          onKeyDown?.(e)
          if (hasPicker && !disabled && !readOnly && e.altKey && e.key === 'ArrowDown') {
            e.preventDefault()
            setOpen(true)
          }
        }}
      />
      <input
        {...rest}
        ref={setRefs}
        id={id}
        type="text"
        tabIndex={-1}
        aria-hidden="true"
        disabled={disabled}
        min={undefined}
        max={undefined}
        className="md-field__native"
        {...(mirrorControlled ? { onChange: rest.onChange ?? noop } : {})}
        onFocus={() => visible.current?.focus()}
      />
      {hasPicker ? (
        <FloatingPanel
          anchorRef={boxRef}
          open={open}
          onClose={closePicker}
          modalOnCompact
          role="dialog"
          aria-label={kind === 'month' ? 'Seleziona mese' : 'Seleziona data'}
          className="md-datepicker"
          onKeyDown={undefined}
        >
          <CalendarPicker
            mode={kind === 'month' ? 'month' : 'date'}
            value={pickerValue}
            min={min?.slice(0, kind === 'month' ? 7 : 10)}
            max={max?.slice(0, kind === 'month' ? 7 : 10)}
            clearable={!rest.required}
            onPick={pickDate}
            onClear={() => {
              setOpen(false)
              setText('')
              commit('')
              visible.current?.focus()
            }}
            onClose={() => {
              setOpen(false)
              visible.current?.focus()
            }}
          />
        </FloatingPanel>
      ) : null}
    </FieldShell>
  )
})
